import { React, useState, useEffect, useMemo, useCallback } from 'react'
import { connect } from 'react-redux'
import UserSelectors from 'Stores/User/Selectors'
import UserActions from 'Stores/User/Actions'
import styles from './UserNotificationList.module.css'
import RequestState from 'Enums/RequestState'
import { IFText, IFTable, IFButton } from 'Components'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { Colors } from 'Theme'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Skeleton } from '@mui/material'
import ReportIcon from '@mui/icons-material/Report'

const UserNotificationList = ({
  userId,
  notifications,
  notificationsFilters,
  fetchUserNotificationList,
  notificationListPaginationOffset,
  fetchUserNotificationListRequestState,
  fetchUserListRequestState,
  tablesHeight,
  selectedUserId,
  loadHeaders,
  selectedUserIndex,
  userDetailsRequestState,
}) => {
  const { t } = useTranslation()
  const [
    fetchNotificationListLoadingState,
    setFetchNotificationListLoadingState,
  ] = useState(true)
  useEffect(() => {
    if (selectedUserId) setFetchNotificationListLoadingState(true)
  }, [selectedUserId])
  useEffect(() => {
    if (fetchUserNotificationListRequestState === RequestState.SUCCEEDED)
      setFetchNotificationListLoadingState(false)
  }, [fetchUserNotificationListRequestState])

  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchUserNotificationListRequestState === RequestState.ERROR_0_NETWORK ||
      fetchUserNotificationListRequestState ===
        RequestState.ERROR_400_OCCURRED ||
      fetchUserNotificationListRequestState ===
        RequestState.ERROR_401_OCCURRED ||
      fetchUserNotificationListRequestState ===
        RequestState.ERROR_403_OCCURRED ||
      fetchUserNotificationListRequestState ===
        RequestState.ERROR_409_OCCURRED ||
      fetchUserNotificationListRequestState ===
        RequestState.ERROR_UNKNOWN_OCCURRED
    )
      setFailedRequest(true)
    if (fetchUserNotificationListRequestState === RequestState.SUCCEEDED)
      setFailedRequest(false)
  }, [fetchUserNotificationListRequestState])

  const loadMoreData = () => {
    if (
      fetchUserNotificationListRequestState === RequestState.LOADING ||
      notificationListPaginationOffset === null
    )
      return
    fetchUserNotificationList(
      selectedUserId,
      notificationsFilters,
      notificationListPaginationOffset,
      failedRequest,
    )
  }

  const handleRetry = () => {
    fetchUserNotificationList(
      userId,
      notificationsFilters,
      notificationListPaginationOffset,
    )
  }

  const headerLoading =
    fetchUserListRequestState === RequestState.LOADING ||
    (fetchUserNotificationListRequestState === RequestState.LOADING &&
      fetchNotificationListLoadingState) ||
    loadHeaders ||
    selectedUserIndex === -1

  const rowReady = (row) =>
    row.index < notifications.length &&
    notifications &&
    userDetailsRequestState === RequestState.SUCCEEDED

  const notificationTableColumns = useMemo(
    () => [
      {
        accessorKey: 'type',
        size: 120,
        header: headerLoading ? (
          <Skeleton variant="text" width="110px" animation="none" height={20} />
        ) : (
          t('UserPage.NotificationTableColumns.Type')
        ),
        Cell: ({ row }) => {
          return rowReady(row) ? (
            <div className={styles.ColumnTitle}>
              <ExpandMoreIcon
                className={styles.ExpandMoreIcon}
                style={{
                  transform: row.getIsExpanded()
                    ? 'rotate(0deg)'
                    : 'rotate(-90deg)',
                }}
              />
              <div className={styles.NotificationType}>
                <IFText>{row.original.type}</IFText>
              </div>
            </div>
          ) : failedRequest ? (
            <div className={styles.IconDiv}>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'title',
        size: 220,
        header: headerLoading ? (
          <Skeleton variant="text" width="110px" animation="none" height={20} />
        ) : (
          t('UserPage.NotificationTableColumns.Title')
        ),
        Cell: ({ row }) => {
          return rowReady(row) ? (
            <IFText>
              {row.original.title?.en || row.original.title?.ar || '-'}
            </IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'isRead',
        size: 120,
        header: headerLoading ? (
          <Skeleton variant="text" width="110px" animation="none" height={20} />
        ) : (
          t('UserPage.NotificationTableColumns.Status')
        ),
        Cell: ({ row }) => {
          return rowReady(row) ? (
            <IFText
              style={{
                color: row.original.isRead ? Colors.gray : Colors.primary,
              }}
            >
              {row.original.isRead
                ? t('UserPage.NotificationTableColumns.Read')
                : t('UserPage.NotificationTableColumns.Unread')}
            </IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'createdAt',
        header: headerLoading ? (
          <Skeleton variant="text" width="110px" animation="none" height={20} />
        ) : (
          t('UserPage.NotificationTableColumns.SentOn')
        ),
        Cell: ({ row }) => {
          return rowReady(row) ? (
            <IFText>
              {moment(row.original.createdAt).format('MMM DD, YYYY LTS')}
            </IFText>
          ) : failedRequest ? (
            <div className={styles.IconDiv}>
              <IFButton
                size="sm"
                color={Colors.red}
                text={t('IFTable.TryAgain')}
                onClick={() => {
                  handleRetry()
                }}
              />
            </div>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
    ],
    [
      notifications,
      fetchUserNotificationListRequestState,
      fetchUserListRequestState,
      failedRequest,
      fetchNotificationListLoadingState,
      loadHeaders,
    ],
  )

  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )

  const detailRow = (label, value, isRtl = false) => (
    <>
      <IFText className={styles.DetailLabel}>{label}</IFText>
      <IFText
        className={styles.DetailValue}
        style={isRtl ? { direction: 'rtl', textAlign: 'right' } : undefined}
      >
        {value || '-'}
      </IFText>
    </>
  )

  const renderDetailPanel = ({ row }) => {
    const n = row.original
    return (
      <div className={styles.DetailPanel}>
        <IFText className={styles.DetailTitle}>
          {t('UserPage.NotificationDetails.Title')}
        </IFText>
        <div className={styles.DetailGrid}>
          {detailRow(t('UserPage.NotificationDetails.TitleEn'), n.title?.en)}
          {detailRow(t('UserPage.NotificationDetails.TitleAr'), n.title?.ar, true)}
          {detailRow(
            t('UserPage.NotificationDetails.MessageEn'),
            n.message?.en,
          )}
          {detailRow(
            t('UserPage.NotificationDetails.MessageAr'),
            n.message?.ar,
            true,
          )}
          {detailRow(t('UserPage.NotificationDetails.Type'), n.type)}
          {detailRow(
            t('UserPage.NotificationDetails.Status'),
            n.isRead
              ? t('UserPage.NotificationTableColumns.Read')
              : t('UserPage.NotificationTableColumns.Unread'),
          )}
          {detailRow(
            t('UserPage.NotificationDetails.RedirectUrl'),
            n.redirectUrl,
          )}
          {detailRow(
            t('UserPage.NotificationDetails.NotificationId'),
            n._id || n.id,
          )}
          {detailRow(
            t('UserPage.NotificationDetails.CreatedAt'),
            moment(n.createdAt).format('MMM DD, YYYY LTS'),
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.tables}>
      <IFTable
        columns={notificationTableColumns}
        data={notifications}
        fetchMoreOnBottomReached={fetchMoreOnBottomReached}
        paginationOffset={notificationListPaginationOffset}
        filters={notificationsFilters}
        showSkeleton={
          (fetchUserNotificationListRequestState === RequestState.LOADING &&
            notificationListPaginationOffset === 0) ||
          (fetchUserNotificationListRequestState !== RequestState.SUCCEEDED &&
            !notificationListPaginationOffset) ||
          selectedUserIndex === -1
        }
        renderDetailPanel={renderDetailPanel}
        tableMaxHeight={tablesHeight}
        tableSetHeight={'100%'}
        tableRequestState={fetchUserNotificationListRequestState}
      />
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchUserNotificationList: (userId, filter, offset, shouldShowError) =>
      dispatch(
        UserActions.fetchUserNotificationList(
          userId,
          filter,
          offset,
          shouldShowError,
        ),
      ),
  }
}

const mapStateToProps = (state) => ({
  selectedUserId: UserSelectors.getSelectedUserId(state),
  selectedUserIndex: UserSelectors.getSelectedUserIndex(state),
  userDetailsRequestState: UserSelectors.getUserDetailsRequestState(state),
  notifications: UserSelectors.getNotificationList(state),
  notificationListPaginationOffset:
    UserSelectors.getNotificationListPaginationOffset(state),
  fetchUserNotificationListRequestState:
    UserSelectors.getFetchUserNotificationListRequestState(state),
  notificationsFilters: UserSelectors.getNotificationsFilters(state),
  fetchUserListRequestState: UserSelectors.getFetchUserListRequestState(state),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UserNotificationList)
